import React from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { FilterState, ProviderId } from '@/lib/trustHub/v1/types';
import { X } from 'lucide-react';
interface FilterPanelProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  queries: string[];
}
export const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFiltersChange,
  queries
}) => {
  const updateFilter = <K extends keyof FilterState,>(key: K, value: FilterState[K]) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };
  const toggleProvider = (provider: ProviderId) => {
    const newProviders = new Set(filters.providers);
    if (newProviders.has(provider)) {
      newProviders.delete(provider);
    } else {
      newProviders.add(provider);
    }
    updateFilter('providers', newProviders);
  };
  return <Card className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Filters & View</h3>
        <Button variant="ghost" size="sm" onClick={() => {
        onFiltersChange({
          providers: new Set(['openai', 'claude']),
          exclusiveMode: false,
          minCoverage: 0,
          accessFilter: 'all',
          tldFilters: {
            gov: false,
            edu: false,
            org: false
          },
          focusedQuery: null,
          consideredWeight: 0,
          sortBy: 'used',
          sortDir: 'desc',
          viewMode: 'table'
        });
      }}>
          <X className="h-4 w-4 mr-1" />
          Reset
        </Button>
      </div>


      {/* Provider Filters */}
      <div className="space-y-2">
        <Label>Providers</Label>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox id="filter-openai" checked={filters.providers.has('openai')} onCheckedChange={() => toggleProvider('openai')} />
            <Label htmlFor="filter-openai" className="cursor-pointer">OpenAI</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id="filter-claude" checked={filters.providers.has('claude')} onCheckedChange={() => toggleProvider('claude')} />
            <Label htmlFor="filter-claude" className="cursor-pointer">Claude</Label>
          </div>
        </div>
        <div className="flex items-center space-x-2 mt-3">
          <Checkbox id="exclusive" checked={filters.exclusiveMode} onCheckedChange={checked => updateFilter('exclusiveMode', checked === true)} disabled={filters.providers.size < 2} />
          <Label htmlFor="exclusive" className="cursor-pointer text-sm">
            Exclusive mode (only domains cited by both)
          </Label>
        </div>
      </div>


      {/* Access Filter */}
      

      {/* TLD Filters */}
      <div className="space-y-2">
        <Label>TLD Filters</Label>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox id="tld-gov" checked={filters.tldFilters.gov} onCheckedChange={checked => updateFilter('tldFilters', {
            ...filters.tldFilters,
            gov: checked === true
          })} />
            <Label htmlFor="tld-gov" className="cursor-pointer">.gov</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id="tld-edu" checked={filters.tldFilters.edu} onCheckedChange={checked => updateFilter('tldFilters', {
            ...filters.tldFilters,
            edu: checked === true
          })} />
            <Label htmlFor="tld-edu" className="cursor-pointer">.edu</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id="tld-org" checked={filters.tldFilters.org} onCheckedChange={checked => updateFilter('tldFilters', {
            ...filters.tldFilters,
            org: checked === true
          })} />
            <Label htmlFor="tld-org" className="cursor-pointer">.org</Label>
          </div>
        </div>
      </div>

      {/* Focused Query */}
      <div className="space-y-2">
        <Label htmlFor="focused-query">Focus on Query</Label>
        <Select value={filters.focusedQuery || 'all'} onValueChange={value => updateFilter('focusedQuery', value === 'all' ? null : value)}>
          <SelectTrigger id="focused-query">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All queries</SelectItem>
            {queries.map((q, idx) => <SelectItem key={idx} value={q}>
                Q{idx + 1}: {q.slice(0, 50)}{q.length > 50 ? '...' : ''}
              </SelectItem>)}
          </SelectContent>
        </Select>
      </div>


    </Card>;
};